import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, User, Mail, Phone, Briefcase, Building, Award, Clock, FileText, CheckCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MentorApplication() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    occupation: "",
    organization: "",
    years_experience: "",
    areas_of_expertise: "",
    why_mentor: "",
    mentoring_experience: "",
    availability: "",
    background_check_consent: false,
    references: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({ ...prev, background_check_consent: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.email || !formData.phone || !formData.why_mentor) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.background_check_consent) {
      toast.error("Please consent to background check to proceed");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : null
      };
      
      await axios.post(`${API}/mentor/apply`, submitData);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      console.error("Error submitting application:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center px-4">
        <div className="max-w-lg w-full text-center">
          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-3xl p-10">
            <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Application Submitted!</h1>
            <p className="text-slate-300 mb-8">
              Thank you for applying to become a Mentor with the 5-Star Peace Pilot. We will review your application and contact you soon.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate("/peace-pilot")}
                className="bg-blue-500 hover:bg-blue-600 text-white font-semibold"
              >
                Back to Peace Pilot
              </Button>
              <Button 
                onClick={() => navigate("/")}
                variant="outline"
                className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
              >
                Go to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="mentor-application-page">
      {/* Hero Section */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d2040] via-[#0a1628] to-[#0a1628]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/peace-pilot")}
            className="text-slate-400 hover:text-white mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Peace Pilot
          </Button>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-['Outfit'] text-white mb-4">
              Mentor Application
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Become a Veteran Community Advisor for the 5-Star Peace Pilot. Guide the next generation of Peace Architects and help shape Milwaukee's future.
            </p>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-10">
            {/* Personal Information */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Personal Information</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">First Name *</Label>
                  <Input
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Your first name"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Last Name *</Label>
                  <Input
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Your last name"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Phone *</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(414) 555-0123"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Background */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Professional Background</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Occupation</Label>
                  <div className="relative mt-1">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="occupation"
                      value={formData.occupation}
                      onChange={handleChange}
                      placeholder="Your current occupation"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Organization/Company</Label>
                  <div className="relative mt-1">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Where you work"
                      className="pl-10 bg-slate-800/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-slate-300">Years of Professional Experience</Label>
                  <Input
                    name="years_experience"
                    type="number"
                    value={formData.years_experience}
                    onChange={handleChange}
                    placeholder="Number of years"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                    min="0"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Areas of Expertise</Label>
                  <Input
                    name="areas_of_expertise"
                    value={formData.areas_of_expertise}
                    onChange={handleChange}
                    placeholder="e.g., Leadership, Conflict Resolution"
                    className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Mentoring Experience */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Mentoring & Motivation</h2>
              </div>

              <div>
                <Label className="text-slate-300">Why do you want to be a Mentor? *</Label>
                <Textarea
                  name="why_mentor"
                  value={formData.why_mentor}
                  onChange={handleChange}
                  placeholder="Tell us why you want to guide the next generation of Peace Architects and what you hope to contribute..."
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white min-h-[120px]"
                  required
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300">Previous Mentoring Experience</Label>
                <Textarea
                  name="mentoring_experience"
                  value={formData.mentoring_experience}
                  onChange={handleChange}
                  placeholder="Describe any previous mentoring, coaching, or teaching experience you have..."
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white min-h-[100px]"
                />
              </div>
            </div>

            {/* Availability & References */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white">Availability & References</h2>
              </div>

              <div>
                <Label className="text-slate-300">Availability</Label>
                <Textarea
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  placeholder="What days and times are you typically available to mentor? (e.g., Weekends, Evenings after 6pm, etc.)"
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                  rows={3}
                />
              </div>

              <div className="mt-4">
                <Label className="text-slate-300">Professional References</Label>
                <Textarea
                  name="references"
                  value={formData.references}
                  onChange={handleChange}
                  placeholder="Please provide 2 professional references (name, relationship, phone/email)"
                  className="mt-1 bg-slate-800/50 border-slate-600 text-white"
                  rows={3}
                />
              </div>
            </div>

            {/* Background Check Consent */}
            <div className="mb-8 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="background_check"
                  checked={formData.background_check_consent}
                  onCheckedChange={handleCheckboxChange}
                  className="mt-1 border-blue-400 data-[state=checked]:bg-blue-500"
                />
                <div>
                  <Label htmlFor="background_check" className="text-white font-medium cursor-pointer">
                    Background Check Consent *
                  </Label>
                  <p className="text-slate-400 text-sm mt-1">
                    I understand that a background check may be required and I consent to this process as part of my application to become a Mentor.
                  </p>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4 border-t border-white/10">
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-6 text-lg"
                disabled={submitting}
              >
                {submitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Submit Mentor Application
                  </>
                )}
              </Button>
              <p className="text-center text-slate-500 text-sm mt-4">
                By submitting this application, you agree to be contacted by Peace for Change Alliance Inc.
              </p>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
